/**
 * Horizon — autoplay product-gallery videos only while they're visible.
 *
 * Works for both the carousel and grid media layouts. Plays the video that is
 * currently on screen (and, in a carousel, the active slide) and pauses the rest.
 * Re-initialises automatically when Horizon swaps the gallery on a variant change.
 */
(function () {
  const PLAY_THRESHOLD = 0.6; // how much of the video must be visible before it plays

  function galleryVideos(root) {
    return Array.from(root.querySelectorAll('.product-media video')).filter(
      (video) => !video.closest('.dialog-zoomed-gallery') // leave the zoom popup alone
    );
  }

  function prepare(video) {
    if (video.dataset.autoplayVisibleReady) return;
    video.dataset.autoplayVisibleReady = 'true';
    // Take control away from native autoplay so off-screen slides don't all fire.
    video.removeAttribute('autoplay');
    video.muted = true;
    video.setAttribute('muted', '');
    video.playsInline = true;
    video.setAttribute('playsinline', '');
  }

  // In a carousel the active slide is the one Horizon marks aria-hidden="false".
  // In a grid there are no slides, so every visible video is eligible.
  function isActiveSlide(video) {
    const slide = video.closest('slideshow-slide');
    if (!slide) return true;
    return slide.getAttribute('aria-hidden') !== 'true';
  }

  function play(video) {
    if (!isActiveSlide(video)) return;
    const result = video.play();
    if (result && typeof result.catch === 'function') result.catch(() => {});
  }

  function pause(video) {
    if (!video.paused) video.pause();
  }

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting && entry.intersectionRatio >= PLAY_THRESHOLD) {
          play(entry.target);
        } else {
          pause(entry.target);
        }
      }
    },
    { threshold: [0, PLAY_THRESHOLD, 1] }
  );

  function setupGallery(gallery) {
    galleryVideos(gallery).forEach((video) => {
      prepare(video);
      observer.observe(video);
    });

    if (gallery.dataset.autoplayVisibleBound) return;
    gallery.dataset.autoplayVisibleBound = 'true';

    // On swipe / arrow / thumbnail change: pause the slide we left, play the new one.
    gallery.addEventListener('slideshow:select', (event) => {
      const slide = event.detail && event.detail.slide;
      galleryVideos(gallery).forEach(pause);
      const current = slide && slide.querySelector('video');
      if (current) play(current);
    });
  }

  function init(root) {
    const galleries =
      root.matches && root.matches('media-gallery')
        ? [root]
        : Array.from(root.querySelectorAll('media-gallery'));
    galleries.forEach(setupGallery);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document));
  } else {
    init(document);
  }

  // Horizon replaces the whole <media-gallery> on variant change — catch new ones.
  new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType !== 1) return;
        if (node.matches && node.matches('media-gallery')) setupGallery(node);
        else if (node.querySelector && node.querySelector('media-gallery')) init(node);
      });
    }
  }).observe(document.body, { childList: true, subtree: true });
})();